import { FRAMES, HUD, artScale, pixelType, type EmoteKind } from "odyssey-shared";
import type { ArtBox } from "../../ui/kit/ArtIcon.tsx";
import { KIT_METRICS } from "../../ui/kit/metrics.ts";
import { pixelTextWidth } from "../../ui/kit/pixel-text.ts";
import { barMustPage, largestChat, slotRect } from "../hud/area-slots.ts";
import type { HudArea, SlotRect } from "../hud/slots.ts";
import { RING, crownAngles, ringPoint, sectorAngle } from "./ring.ts";

/**
 * Where the radial emote wheel stands and how its parts are laid out (P2.19-T11; R-0237; the design's `emotes` card,
 * `EmoteWheelSurfaces.jsx`). Pure: the panel draws from these, whole logical pixels throughout.
 *
 * On an area where the chat panel stands beside the ability bar (632 logical pixels wide and more) the ring is
 * centred across the area, on the character, with the plaque of Send and Close under it, raised so the plaque ends
 * where the cast bar begins, as the card stands it on the 640 by 360 grid. On a narrower area the plaque stands beside
 * the ring, hung from the right margin, the ring a gap left of it and the crown closed up, so the ring clears the chat
 * panel at its largest. On either, the crown never leaves the top margin and nothing reaches the XP bar over the
 * ability bar; an area too small for the card's ring takes the largest ring that fits, the band between its radii
 * kept, down to `MIN_OUTER`. An area too small even for that (445 by 230, beside the chat slot's 198) takes
 * `MIN_OUTER` where it would stand, over the chat panel's right edge.
 */

/**
 * The plaque's measures: an ink edge and a brass line inside it, `padding` inside those, `hintGap` between a key glyph
 * and its word, and between the two hints `rowGap` side by side or `columnGap` one above the other.
 */
export const PLAQUE = { edge: 2, padding: 1, paddingX: 3, hintGap: 3, rowGap: 8, columnGap: 3 } as const;

/**
 * The smallest outer radius the wheel shrinks to on an area too small for the card's: the smallest whose hub holds every
 * page's name and every entry's word inside its notch (`hubText`).
 */
export const MIN_OUTER = 86;

/**
 * The labels the wheel's key glyphs show: the page keys' (null for a key the layer does not name, which is not
 * drawn), Enter's, and the key Emotes is bound to, which closes it.
 */
export interface WheelKeys {
  readonly before: string | null;
  readonly after: string | null;
  readonly send: string;
  readonly close: string;
}

/**
 * The words on the plaque: its two hints, and the line it adds while a text field owns the keyboard.
 */
export interface PlaqueWords {
  readonly send: string;
  readonly close: string;
  readonly typing: string;
}

/**
 * The wheel laid out on an area. `box` is on the area; every other rectangle and the centre are from the box's top
 * left.
 */
export interface WheelLayout {
  readonly box: SlotRect;
  readonly centre: { readonly x: number; readonly y: number };
  readonly outer: number;
  readonly inner: number;
  readonly beside: boolean;
  readonly span: number;
  readonly markers: readonly SlotRect[];
  readonly before: SlotRect | null;
  readonly after: SlotRect | null;
  readonly plaque: SlotRect;
  /** The plaque while a text field owns the keyboard, its typing line added: Send and Close stay where they were. */
  readonly plaqueBusy: SlotRect;
}

/**
 * A key glyph's width for a legend, the kit's: the key frame's insets and its padding either side of the legend.
 *
 * @param legend The legend.
 * @returns The width in logical pixels.
 * @example
 * keyGlyphWidth("Q");
 */
export function keyGlyphWidth(legend: string): number {
  const insets = FRAMES.button.insets;
  return insets.left + KIT_METRICS.key.paddingX + pixelTextWidth(legend) + KIT_METRICS.key.paddingX + insets.right;
}

/**
 * One hint's width: its key glyph, the gap and its word.
 *
 * @param key The key's legend.
 * @param word The word.
 * @returns The width in logical pixels.
 */
function hintWidth(key: string, word: string): number {
  return keyGlyphWidth(key) + PLAQUE.hintGap + pixelTextWidth(word);
}

/**
 * The plaque's size: its two hints side by side under the ring, or one above the other beside it, and while a text
 * field owns the keyboard its typing line after them.
 *
 * @param keys The key glyphs' legends.
 * @param words The plaque's words.
 * @param beside Whether the plaque stands beside the ring.
 * @param busy Whether the typing line is shown.
 * @returns The width and height in logical pixels.
 * @example
 * plaqueSize({ before: "Q", after: "E", send: "Enter", close: "H" }, { send: "Send", close: "Close", typing: "Typing" }, false, false);
 */
export function plaqueSize(keys: WheelKeys, words: PlaqueWords, beside: boolean, busy: boolean): { readonly width: number; readonly height: number } {
  const send = hintWidth(keys.send, words.send);
  const close = hintWidth(keys.close, words.close);
  const typing = pixelTextWidth(words.typing);
  const sides = 2 * (PLAQUE.edge + PLAQUE.paddingX);
  const ends = 2 * (PLAQUE.edge + PLAQUE.padding);
  const key = KIT_METRICS.key.height;
  if (beside) {
    return busy ? { width: sides + Math.max(send, close, typing), height: ends + 2 * key + 2 * PLAQUE.columnGap + pixelType("label").lineHeight } : { width: sides + Math.max(send, close), height: ends + 2 * key + PLAQUE.columnGap };
  }
  return { width: sides + send + PLAQUE.rowGap + close + (busy ? PLAQUE.rowGap + typing : 0), height: ends + key };
}

/**
 * A rectangle centred on a point, its corner rounded down to a whole pixel.
 *
 * @param x The point across.
 * @param y The point down.
 * @param width The width.
 * @param height The height.
 * @returns The rectangle.
 */
function centredOn(x: number, y: number, width: number, height: number): SlotRect {
  return { left: x - Math.floor(width / 2), top: y - Math.floor(height / 2), width, height };
}

/**
 * The crown's parts round a centre at 0, 0: a marker a page and the two page keys at the arc's ends.
 *
 * @param outer The ring's outer radius.
 * @param pages How many pages.
 * @param span The degrees between markers.
 * @param keys The key glyphs' legends.
 * @returns The markers and the keys, each null where its key is not drawn.
 */
function crownParts(outer: number, pages: number, span: number, keys: WheelKeys): { readonly markers: SlotRect[]; readonly before: SlotRect | null; readonly after: SlotRect | null } {
  const radius = outer + RING.crownGap;
  const angles = crownAngles(pages, span);
  const markers = angles.markers.map((degrees) => {
    const point = ringPoint(degrees, radius);
    return centredOn(point.x, point.y, RING.marker, RING.marker);
  });
  const key = (legend: string | null, degrees: number): SlotRect | null => {
    if (legend === null) {
      return null;
    }
    const point = ringPoint(degrees, radius);
    return centredOn(point.x, point.y, keyGlyphWidth(legend), KIT_METRICS.key.height);
  };
  return { markers, before: key(keys.before, angles.before), after: key(keys.after, angles.after) };
}

/**
 * The smallest rectangle holding every given one.
 *
 * @param rects The rectangles; at least one.
 * @returns Their union.
 */
function union(rects: readonly SlotRect[]): SlotRect {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const rect of rects) {
    left = Math.min(left, rect.left);
    top = Math.min(top, rect.top);
    right = Math.max(right, rect.left + rect.width);
    bottom = Math.max(bottom, rect.top + rect.height);
  }
  return { left, top, width: right - left, height: bottom - top };
}

/**
 * A rectangle moved by an offset.
 *
 * @param rect The rectangle.
 * @param x The offset across.
 * @param y The offset down.
 * @returns The rectangle moved.
 */
function moved(rect: SlotRect, x: number, y: number): SlotRect {
  return { left: rect.left + x, top: rect.top + y, width: rect.width, height: rect.height };
}

/**
 * A plaque's size.
 */
interface PlaqueBox {
  readonly width: number;
  readonly height: number;
}

/**
 * The wheel's parts on an area.
 */
interface WheelParts {
  readonly ring: SlotRect;
  readonly markers: readonly SlotRect[];
  readonly before: SlotRect | null;
  readonly after: SlotRect | null;
  readonly plaque: SlotRect;
  readonly plaqueBusy: SlotRect;
}

/**
 * The slots the wheel never covers on any area: the chat panel at its largest, the player's card and effect row, the
 * XP bar, the ability bar, the stance button and the menu button.
 */
const KEPT_CLEAR = ["vitals", "status", "xp", "abilities", "stance", "menu"] as const;

/**
 * Whether two rectangles share a pixel.
 *
 * @param a One rectangle.
 * @param b The other.
 * @returns True when they overlap.
 */
function overlaps(a: SlotRect, b: SlotRect): boolean {
  return a.left < b.left + b.width && b.left < a.left + a.width && a.top < b.top + b.height && b.top < a.top + a.height;
}

/**
 * How far an entry's label band reaches past the ring's square at its sides: a label straight across from the centre,
 * as the second and fourth of a page of four stand, is `RING.labelWidth` wide on the ring's middle.
 *
 * @param outer The outer radius.
 * @returns The overhang either side in logical pixels, 0 when the band stays inside the ring.
 */
function labelOverhang(outer: number): number {
  const middle = Math.round((outer + outer - (RING.outer - RING.inner)) / 2);
  return Math.max(0, middle + Math.ceil(RING.labelWidth / 2) - outer);
}

/**
 * The wheel's parts on an area at one outer radius, centred at a point.
 *
 * @param x The ring's centre across, on the area.
 * @param y The ring's centre down, on the area.
 * @param outer The outer radius.
 * @param beside Whether the plaque stands beside the ring.
 * @param crown The crown's parts round 0, 0.
 * @param plaque The plaque's size.
 * @param busy The plaque's size with its typing line, which beside the ring keeps its right edge where the plaque's
 *   is, at the margin, and reaches into the gap to the ring.
 * @returns Every part's rectangle on the area.
 */
function partsAt(x: number, y: number, outer: number, beside: boolean, crown: ReturnType<typeof crownParts>, plaque: PlaqueBox, busy: PlaqueBox): WheelParts {
  const reach = outer + labelOverhang(outer);
  const top = beside ? y - Math.floor(plaque.height / 2) : y + outer + 1;
  const left = (size: PlaqueBox): number => (beside ? x + outer + RING.plaqueGap + plaque.width - size.width : x - Math.floor(size.width / 2));
  return {
    ring: { left: x - reach, top: y - outer, width: 2 * reach, height: 2 * outer },
    markers: crown.markers.map((marker) => moved(marker, x, y)),
    before: crown.before === null ? null : moved(crown.before, x, y),
    after: crown.after === null ? null : moved(crown.after, x, y),
    plaque: { left: left(plaque), top, ...plaque },
    plaqueBusy: { left: left(busy), top, ...busy },
  };
}

/**
 * Every part of the wheel on an area as one list: the ring's square, both looks of the plaque, the markers and the
 * page keys.
 *
 * @param parts The parts.
 * @returns Their rectangles.
 */
function everyPart(parts: WheelParts): SlotRect[] {
  return [parts.ring, parts.plaque, parts.plaqueBusy, ...crownOf(parts)];
}

/**
 * The crown's parts on an area: the markers and the page keys drawn.
 *
 * @param parts The parts.
 * @returns Their rectangles.
 */
function crownOf(parts: Pick<WheelParts, "markers" | "before" | "after">): SlotRect[] {
  return [...parts.markers, ...(parts.before === null ? [] : [parts.before]), ...(parts.after === null ? [] : [parts.after])];
}

/**
 * The layout from the parts on an area: their union as the box, every part from the box's corner.
 *
 * @param parts The parts on the area.
 * @param centre The ring's centre on the area.
 * @param outer The outer radius.
 * @param beside Whether the plaque stands beside the ring.
 * @param span The crown's span.
 * @returns The layout.
 */
function layoutOf(parts: WheelParts, centre: { readonly x: number; readonly y: number }, outer: number, beside: boolean, span: number): WheelLayout {
  const box = union(everyPart(parts));
  const local = (rect: SlotRect): SlotRect => moved(rect, -box.left, -box.top);
  return {
    box,
    centre: { x: centre.x - box.left, y: centre.y - box.top },
    outer,
    inner: outer - (RING.outer - RING.inner),
    beside,
    span,
    markers: parts.markers.map(local),
    before: parts.before === null ? null : local(parts.before),
    after: parts.after === null ? null : local(parts.after),
    plaque: local(parts.plaque),
    plaqueBusy: local(parts.plaqueBusy),
  };
}

/**
 * How far down the ring's centre must stand for no part of the crown to cover the target's frame: the crown standing
 * across the frame's columns ends at or under its bottom.
 *
 * @param crown The crown's parts round 0, 0.
 * @param x The ring's centre across, on the area.
 * @param target The target frame's slot.
 * @returns The least centre down, or -Infinity when no part stands across the frame.
 */
function underTarget(crown: ReturnType<typeof crownParts>, x: number, target: SlotRect): number {
  let least = -Infinity;
  for (const part of crownOf(crown)) {
    if (x + part.left < target.left + target.width && target.left < x + part.left + part.width) {
      least = Math.max(least, target.top + target.height - part.top);
    }
  }
  return least;
}

/**
 * How `placed` treats a ring: `clear` takes it only where it fits with its crown clear of the target's frame, `fit`
 * where it fits at all, and `loose` anywhere, the smallest ring's last resort.
 */
type Placing = "clear" | "fit" | "loose";

/**
 * The wheel on an area at one outer radius: the centre across on a wide area, the plaque hung from the right margin
 * on a narrow one; the centre down at the area's middle, raised until the parts under the ring end at the cast bar,
 * and never so high the crown leaves the top margin. Where that leaves the crown over the target's frame, as it does
 * on a short area where the cast bar cannot be cleared, the centre goes down until the crown clears the frame, when
 * the ring still fits there; under `fit`, a ring that does not fit there keeps the higher centre over the frame. The
 * frame cleared is the slot and, while a target is shown, the frame as drawn, which a long name widens.
 *
 * @param area The area.
 * @param outer The outer radius.
 * @param pages How many pages.
 * @param keys The key glyphs' legends.
 * @param words The plaque's words.
 * @param placing Whether to refuse a ring whose crown covers the target's frame (`clear`), only one that reaches a
 *   slot of `KEPT_CLEAR`, the chat panel at its largest or the area's margins (`fit`), or none (`loose`, which also
 *   leaves the target's frame alone).
 * @param target The target frame as drawn on the area, or null while none is shown.
 * @returns The layout, or null for a refused ring.
 */
function placed(area: HudArea, outer: number, pages: number, keys: WheelKeys, words: PlaqueWords, placing: Placing, target: SlotRect | null): WheelLayout | null {
  const strict = placing !== "loose";
  const across = Math.floor(area.width);
  const down = Math.floor(area.height);
  const beside = barMustPage({ width: across, height: down });
  const span = beside || !strict ? RING.spanBeside : RING.span;
  const plaque = plaqueSize(keys, words, beside, false);
  const busy = plaqueSize(keys, words, beside, true);
  const crown = crownParts(outer, pages, span, keys);
  const rise = Math.max(outer, ...crownOf(crown).map((part) => -part.top));
  const below = beside ? outer : outer + 1 + plaque.height;
  const x = beside ? across - HUD.margin - plaque.width - RING.plaqueGap - outer : Math.floor(across / 2);
  const y = Math.max(HUD.margin + rise, Math.min(Math.floor(down / 2), slotRect("cast", area).top - below));
  if (!strict) {
    return layoutOf(partsAt(x, y, outer, beside, crown, plaque, busy), { x, y }, outer, beside, span);
  }
  const lowered = Math.max(y, underTarget(crown, x, slotRect("target", area)), target === null ? -Infinity : underTarget(crown, x, target));
  if (placing === "clear") {
    return fitted(area, x, lowered, outer, beside, span, crown, plaque, busy);
  }
  return (lowered > y ? fitted(area, x, lowered, outer, beside, span, crown, plaque, busy) : null) ?? fitted(area, x, y, outer, beside, span, crown, plaque, busy);
}

/**
 * The wheel at one centre when it fits there: inside the area's margins and clear of the chat panel at its largest
 * and every slot of `KEPT_CLEAR`.
 *
 * @param area The area.
 * @param x The ring's centre across.
 * @param y The ring's centre down.
 * @param outer The outer radius.
 * @param beside Whether the plaque stands beside the ring.
 * @param span The crown's span.
 * @param crown The crown's parts round 0, 0.
 * @param plaque The plaque's size.
 * @param busy The plaque's size with its typing line.
 * @returns The layout, or null where it does not fit.
 */
function fitted(area: HudArea, x: number, y: number, outer: number, beside: boolean, span: number, crown: ReturnType<typeof crownParts>, plaque: PlaqueBox, busy: PlaqueBox): WheelLayout | null {
  const parts = partsAt(x, y, outer, beside, crown, plaque, busy);
  const layout = layoutOf(parts, { x, y }, outer, beside, span);
  const kept = [largestChat(area), ...KEPT_CLEAR.map((id) => slotRect(id, area))];
  const { box } = layout;
  const inside = box.left >= HUD.margin && box.top >= HUD.margin && box.left + box.width <= Math.floor(area.width) - HUD.margin && box.top + box.height <= Math.floor(area.height) - HUD.margin;
  return inside && !everyPart(parts).some((part) => kept.some((slot) => overlaps(part, slot))) ? layout : null;
}

/**
 * The wheel laid out on an area: the card's ring where it fits clear of the chat panel at its largest, the player's
 * card and effect row, the XP and ability bars, the stance button and the menu button, else the largest ring that
 * does, down to `MIN_OUTER`. The largest ring that fits with its crown clear of the target's frame comes first; only
 * where no ring does is the largest that fits taken with its crown over the frame. The frame is the slot and, while a
 * target is shown, the frame as drawn, so a long name can lower or shrink the ring and a short one leaves it where it
 * stands with no target. On an area too small even for that, `MIN_OUTER` standing where a ring stands on an area of
 * its width, whatever it covers there.
 *
 * @param area The HUD's area, whole logical pixels.
 * @param pages How many pages the wheel shows, a marker each.
 * @param keys The key glyphs' legends.
 * @param words The plaque's words.
 * @param target The target frame as drawn on the area (`targetFrameRect`), or null while none is shown.
 * @returns The layout.
 * @example
 * emoteWheelLayout({ width: 640, height: 360 }, 5, { before: "Q", after: "E", send: "Enter", close: "H" }, { send: "Send", close: "Close", typing: "Typing" }, null);
 */
export function emoteWheelLayout(area: HudArea, pages: number, keys: WheelKeys, words: PlaqueWords, target: SlotRect | null = null): WheelLayout {
  for (const placing of ["clear", "fit"] as const) {
    for (let outer = RING.outer; outer >= MIN_OUTER; outer -= 1) {
      const layout = placed(area, outer, pages, keys, words, placing, target);
      if (layout !== null) {
        return layout;
      }
    }
  }
  return placed(area, MIN_OUTER, pages, keys, words, "loose", target) as WheelLayout;
}

/**
 * The hub's text: the page's name over the selected entry's word (the author, 2026-10-08: "the name is enough"). The
 * word is the panel title's size when it has `LARGE_WORD` letters or fewer and fits, else the label's, wrapped; the
 * two stand a gap apart, as low or high in the hub as keeps every line inside the notch on the hub's rim.
 */
export interface HubText {
  /** Whether the word is drawn at the panel title's size. */
  readonly large: boolean;
  /** The widest a line of the word may be, where it wraps. */
  readonly width: number;
  /** The page's name and the word, from the wheel's box, as tall as their lines and their gap. */
  readonly box: SlotRect;
  /** Each line from the wheel's box: the page's name, then the word's. */
  readonly lines: readonly SlotRect[];
}

/**
 * The most letters a word drawn at the panel title's size in the hub has, the card's.
 */
export const LARGE_WORD = 7;

/**
 * The gap between the page's name and the word in the hub.
 */
export const HUB_GAP = 2;

/**
 * How far inside the hub's edge its text stays: the ink edge, the brass rim and the notch on it.
 */
const HUB_INSET = 4;

/**
 * The lines of a stack centred across on 0, its top at a given height.
 *
 * @param widths Each line's width.
 * @param heights Each line's height.
 * @param top The stack's top.
 * @returns Each line's rectangle round 0, 0.
 */
function stacked(widths: readonly number[], heights: readonly number[], top: number): SlotRect[] {
  let y = top;
  return widths.map((width, index) => {
    const line = { left: -width / 2, top: y, width, height: heights[index] as number };
    y += line.height + (index === 0 ? HUB_GAP : 0);
    return line;
  });
}

/**
 * The farthest any corner of a stack's lines lies from 0, 0.
 *
 * @param lines The lines round 0, 0.
 * @returns The distance.
 */
function farthest(lines: readonly SlotRect[]): number {
  let far = 0;
  for (const line of lines) {
    far = Math.max(far, Math.hypot(line.width / 2, Math.max(Math.abs(line.top), Math.abs(line.top + line.height))));
  }
  return far;
}

/**
 * The top at which a stack of lines lies nearest the centre, on a whole pixel, and how far its farthest corner is.
 *
 * @param widths Each line's width.
 * @param heights Each line's height.
 * @returns The top and the distance.
 */
function bestTop(widths: readonly number[], heights: readonly number[]): { readonly top: number; readonly far: number } {
  const total = heights.reduce((sum, height) => sum + height, 0) + HUB_GAP;
  let best = { top: -Math.ceil(total / 2), far: Infinity };
  for (let top = -total; top <= 0; top += 1) {
    const far = farthest(stacked(widths, heights, top));
    if (far < best.far) {
      best = { top, far };
    }
  }
  return best;
}

/**
 * The hub's text on a layout: the word at the panel title's size where it fits, else at the label's wrapped as wide as
 * keeps it inside the hub; on a ring too small for either, at the label's wrapped to the entries' band, centred.
 *
 * @param layout The wheel's layout.
 * @param page The page's name.
 * @param word The selected entry's word.
 * @returns The text's size, wrap width and lines.
 * @example
 * hubText(emoteWheelLayout({ width: 640, height: 360 }, 5, keys, words), "Interactions", "Threaten");
 */
export function hubText(layout: WheelLayout, page: string, word: string): HubText {
  const limit = layout.inner - RING.band - HUB_INSET;
  const label = pixelType("label").lineHeight;
  const title = pixelType("panel-title").lineHeight;
  const scale = pixelType("panel-title").size / pixelType("label").size;
  const name = pixelTextWidth(page);
  /**
   * The hub's text with the word at one size and wrap, its stack's top at a height from the ring's centre.
   *
   * @param large Whether the word is at the panel title's size.
   * @param width The widest a line of the word may be.
   * @param lines The word's lines.
   * @param top The stack's top from the ring's centre.
   * @returns The text on the wheel's box.
   */
  const placedAt = (large: boolean, width: number, lines: readonly string[], top: number): HubText => {
    const widths = [name, ...lines.map((line) => pixelTextWidth(line) * (large ? scale : 1))];
    const heights = [label, ...lines.map(() => (large ? title : label))];
    const shifted = stacked(widths, heights, top).map((line) => ({ left: layout.centre.x + line.left, top: layout.centre.y + line.top, width: line.width, height: line.height }));
    const wide = Math.max(...widths);
    const tall = heights.reduce((sum, height) => sum + height, 0) + HUB_GAP;
    return { large, width, box: { left: layout.centre.x - Math.ceil(wide / 2), top: layout.centre.y + top, width: 2 * Math.ceil(wide / 2), height: tall }, lines: shifted };
  };
  if (word.length <= LARGE_WORD) {
    const wide = pixelTextWidth(word) * scale;
    const fit = bestTop([name, wide], [label, title]);
    if (fit.far <= limit) {
      return placedAt(true, wide, [word], fit.top);
    }
  }
  for (let width = 2 * limit; width >= RING.labelWidth / 2; width -= 1) {
    const lines = wrapText(word, width);
    if (lines.some((line) => pixelTextWidth(line) > width)) {
      continue;
    }
    const fit = bestTop([name, ...lines.map((line) => pixelTextWidth(line))], [label, ...lines.map(() => label)]);
    if (fit.far <= limit) {
      return placedAt(false, width, lines, fit.top);
    }
  }
  const lines = wrapText(word, RING.labelWidth);
  const tall = label * (lines.length + 1) + HUB_GAP;
  return placedAt(false, RING.labelWidth, lines, -Math.ceil(tall / 2));
}

/**
 * The box an emoji's cell on the wheel draws its art in at a UI scale (P2.19-T9; ADR-114 decision 1.3, and its note for
 * P2.19-T9 after decision 5.3): ADR-111's 22 box wherever that holds the art at a whole number of device pixels an art
 * pixel, else the 32 box, one device pixel an art pixel, so an emoji is never left without its picture.
 *
 * @param scale The UI scale d.
 * @returns 22 from UI scale 2, 32 at UI scale 1.
 * @example
 * emojiBox(1);
 * // 32
 */
export function emojiBox(scale: number): Extract<ArtBox, 22 | 32> {
  return artScale(22, scale) > 0 ? 22 : 32;
}

/**
 * Where each entry stands, centred on the ring's middle at its sector's angle: an action's label on a band
 * `RING.labelWidth` wide, as many lines of the pixel face tall as its word wraps onto there; an emoji's cell a square of
 * the box its art is drawn in.
 *
 * @param layout The wheel's layout.
 * @param entries The entries' kinds and words, in page order.
 * @param cell The side of an emoji's cell (`emojiBox`).
 * @returns Each entry's rectangle from the wheel's box.
 * @example
 * entryBoxes(layout, [{ kind: "emoji", word: "Happy" }], 22);
 */
export function entryBoxes(layout: WheelLayout, entries: readonly { readonly kind: EmoteKind; readonly word: string }[], cell: number): readonly SlotRect[] {
  const middle = Math.round((layout.outer + layout.inner) / 2);
  const line = pixelType("label").lineHeight;
  return entries.map((entry, index) => {
    const point = ringPoint(sectorAngle(index, entries.length), middle);
    const x = layout.centre.x + point.x;
    const y = layout.centre.y + point.y;
    return entry.kind === "emoji" ? centredOn(x, y, cell, cell) : centredOn(x, y, RING.labelWidth, wrappedLines(entry.word, RING.labelWidth) * line);
  });
}

/**
 * The lines a text wraps onto in a width, breaking at spaces as the browser does, a word wider than the width keeping
 * a line to itself.
 *
 * @param text The text.
 * @param width The width in logical pixels.
 * @returns The lines, at least one.
 * @example
 * wrapText("Challenge gesture", 58);
 * // ["Challenge", "gesture"]
 */
export function wrapText(text: string, width: number): string[] {
  const space = pixelTextWidth(" ");
  const lines: string[] = [];
  let line = "";
  let used = 0;
  for (const word of text.split(" ")) {
    const wide = pixelTextWidth(word);
    if (line !== "" && used + space + wide > width) {
      lines.push(line);
      line = word;
      used = wide;
    } else {
      line = line === "" ? word : `${line} ${word}`;
      used += (used > 0 ? space : 0) + wide;
    }
  }
  lines.push(line);
  return lines;
}

/**
 * How many lines a word wraps onto in a width (`wrapText`).
 *
 * @param text The text.
 * @param width The width in logical pixels.
 * @returns The lines, at least one.
 * @example
 * wrappedLines("Challenge gesture", 58);
 * // 2
 */
export function wrappedLines(text: string, width: number): number {
  return wrapText(text, width).length;
}
